import {
  FontWeights,
  ILabelStyles,
  ILinkStyles,
  IProcessedStyleSet,
  IStyle,
  concatStyleSets,
  mergeStyleSets
} from 'office-ui-fabric-react';

export namespace AppLayoutSettings {

  export namespace AppImageParams {

    /** Width [in pixels] of the cartoon image, to be shown on Transcription Pane. */
    export const cartoonImageWidthPx = '200px';

    /** Height [in pixels] of the cartoon image, to be shown on Transcription Pane. */
    export const cartoonImageHeightPx = '142px';

    /** Name of the cartoon image, to be shown on Transcription Pane. */
    export const cartoonImageName = 'Illustration-Transcribing.svg';

    /** Width [in pixels] of the failure image, to be shown on Transcription Pane. */
    export const failureImageWidthPx = "142px";

    /** Height [in pixels] of the failure image, to be shown on Transcription Pane. */
    export const failureImageHeightPx = "131px";

    /** Name of the failure image, to be shown on Transcription Pane. */
    export const failureImageName = 'Illustration-Error.svg';
  }

  export enum AppColors {

    /** Color for all the primary and important text labels. */
    darkLabelColor = '#212121',

    /** Color for all the medium important text labels. */
    mediumLabelColor = '#303030',

    /** Color for all the light and less important text labels. */
    lightLabelColor = '#6e6e6e',

    /** Color for all the timestamp labels used in application. */
    timestampLabelColor = '#11100f',

    /** Color for all the links in application. */
    linkColor = '#2b579a',

    /** Color for default background in application. */
    defaultBackgroundColor = '#edebe9',

    /** Color for light background in application. */
    lightBackgroundColor = '#faf9f8',

    /** Color for dark background in application. */
    darkBackgroundColor = '#cccccc',

    /** Primary color for all widgets in application. */
    primaryWidgetColor = '#2b579a',

    /** Primary color for label of all widgets in application. */
    primaryWidgetLabelColor = '#ffffff',

    /** Absolute black color. */
    blackColor = '#000000',

    /** Absolute white color. */
    whiteColor = '#ffffff',

    /** Color to render the upsell diamond icon - also used by Editor */
    wordBlue = '#2e6ac5'
  }

  export enum AppFontSize {

    /** Alternate font size in pixels for extra large font display. */
    extraLargePlusFontSizePx = '17px',

    /** Font size in pixels for extra large font display. */
    extraLargeFontSizePx = '16px',

    /** Alternate font size in pixels for large font display. */
    largePlusFontSizePx = '15px',

    /** Font size in pixels for large font display. */
    largeFontSizePx = '14px',

    /** Font size in pixels for medium font display. */
    mediumFontSizePx = '12px',

    /** Alternate font size in pixels for small font display. */
    smallPlusFontSizePx = '11px',

    /** Font size in pixels for small font display. */
    smallFontSizePx = '10px'
  }

  export enum AppDisplayType {

    /** Displays an element as a block element (like <p>). */
    block = 'block',

    /** Displays an element as an inline element (like <span>). */
    inline = 'inline',

    /** Displays an element as an inline-level block container. */
    inlineBlock = 'inline-block',

    /** Displays an element as a block-level flex container. */
    flex = 'flex',

    /** Inherits this property from its parent element. */
    inherit = 'inherit',

    /** The element is hidden. */
    none = 'none'
  }

  export namespace AppCommonStyles {

    export const getPageRootContainersCSSProperties = (): React.CSSProperties => {
      /** Common padding, in pixels, to be used across containers. */
      const containerSidesPaddingSizePx = 16;

      const CSSProperties: React.CSSProperties = {
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: `${containerSidesPaddingSizePx}px`,
        paddingRight: `${containerSidesPaddingSizePx}px`,
        flexDirection: 'column',
        display: 'flex',
        height: '100%',
      };

      return CSSProperties;
    };

    export const getLabelStyles = (fontSizePx: AppFontSize, fontColor: AppColors): ILabelStyles => {
      const styles: ILabelStyles = {
        root: {
          fontSize: fontSizePx,
          color: fontColor
        }
      };

      return styles;
    };

    export const getRegularLabelStyles = (fontSizePx: AppFontSize, fontColor: AppColors): ILabelStyles => {
      const commonLabelStyles = AppLayoutSettings.AppCommonStyles.getLabelStyles(fontSizePx, fontColor);
      const regularLabelStyles: ILabelStyles = {
        root: {
          fontWeight: FontWeights.regular
        }
      };

      return concatStyleSets(commonLabelStyles, regularLabelStyles);
    };

    export const getBoldLabelStyles = (fontSizePx: AppFontSize, fontColor: AppColors): ILabelStyles => {
      const commonLabelStyles = AppLayoutSettings.AppCommonStyles.getLabelStyles(fontSizePx, fontColor);
      const boldLabelStyles: ILabelStyles = {
        root: {
          fontWeight: FontWeights.semibold
        }
      };

      return concatStyleSets(commonLabelStyles, boldLabelStyles);
    };

    export const getPageTopLabelStyles = (): ILabelStyles => {
      const commonLabelStyles = AppLayoutSettings.AppCommonStyles.getRegularLabelStyles(AppLayoutSettings.AppFontSize.mediumFontSizePx, AppLayoutSettings.AppColors.mediumLabelColor);
      const specificStyle: ILabelStyles = {
        root: {
          paddingTop: '0px'
        }
      };

      return concatStyleSets(commonLabelStyles, specificStyle);
    };

    export const getLinkStyles = (fontSizePx: AppFontSize, fontColor: AppColors): ILinkStyles => {
      const styles: ILinkStyles = {
        root: {
          fontSize: fontSizePx,
          fontWeight: FontWeights.semibold,
          color: fontColor,
          selectors: {
            [`:focus`]: {
              color: fontColor
            },
            [`:active , :hover`]: {
              color: fontColor,
              textDecoration: 'underline'
            }
          }
        }
      };

      return styles;
    };

    export const getFileInputStyles = (displayType: AppDisplayType) => {
      const styles = {
        display: displayType
      }

      return styles;
    };

    export const getLearnMoreLinkStyles = (): ILinkStyles => {
      return AppLayoutSettings.AppCommonStyles.getLinkStyles(AppLayoutSettings.AppFontSize.mediumFontSizePx, AppLayoutSettings.AppColors.linkColor);
    };

  }
}

// Styles for the "main" (a.k.a. Landing) page.
// Used both for Landing and Failure pages.
export interface MainPageStyles {
  labelContainer: IStyle;
  buttonContainer: IStyle;
  imageContainer: IStyle;
  bottomLabelContainer: IStyle;
  progressContainer: IStyle;
}

export const getMainPageStyles = (): IProcessedStyleSet<MainPageStyles> => {
  const styles: MainPageStyles = {
    labelContainer: {
      width: '100%'
    },
    buttonContainer: {
      marginTop: '31px',
      display: 'flex',
      justifyContent: 'space-around',
      width: '100%'
    },
    imageContainer: {
      marginTop: '66px',
      display: 'flex',
      justifyContent: 'center',
      width: '100%'
    },
    bottomLabelContainer: {
      paddingBottom: '28px'
    },
    progressContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-evenly',
      width: '50%'
    }
  };

  return mergeStyleSets(styles);
};
